import { useEffect, useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Chaque page a son propre conteneur scrollable (`h-screen` + `overflow-auto`), le
// navigateur ne peut donc pas restaurer le scroll tout seul. On mémorise, par URL, la
// position du dernier élément scrollé (identifié par son chemin d'index depuis <body>) et
// on la réapplique au retour : bouton précédent / navigate(-1) (POP), ou navigation avec
// `state.restoreScroll` (boutons « Retour » qui font un navigate vers l'URL d'origine).
// ponytail: positions en mémoire (perdues au F5), sessionStorage si besoin.
const positions = new Map();
const RESTORE_TIMEOUT_MS = 3000; // le temps que les données async remplissent la page

const urlKey = (location) => location.pathname + location.search;

function pathOf(el) {
  const path = [];
  while (el && el !== document.body && el.parentElement) {
    path.unshift(Array.prototype.indexOf.call(el.parentElement.children, el));
    el = el.parentElement;
  }
  return el === document.body ? path : null;
}

function elementAt(path) {
  let el = document.body;
  for (const i of path) {
    el = el?.children[i];
  }
  return el || null;
}

export default function ScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const key = urlKey(location);

  useEffect(() => {
    const onScroll = (e) => {
      const target = e.target === document ? document.scrollingElement : e.target;
      if (!(target instanceof Element)) return;
      const path = target === document.scrollingElement ? "window" : pathOf(target);
      if (path) positions.set(key, { path, top: target.scrollTop });
    };
    // capture : l'événement scroll ne remonte pas, mais se capture depuis document.
    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => document.removeEventListener("scroll", onScroll, { capture: true });
  }, [key]);

  useLayoutEffect(() => {
    const saved = positions.get(key);
    const wantsRestore = navigationType === "POP" || location.state?.restoreScroll;
    if (!saved || !wantsRestore) return undefined;

    let raf;
    const start = Date.now();
    const stop = () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchstart", stop);
      window.removeEventListener("keydown", stop);
    };
    const tick = () => {
      const el = saved.path === "window" ? document.scrollingElement : elementAt(saved.path);
      if (el) {
        el.scrollTop = saved.top;
        // Atteint (ou plus rien à charger) : on arrête de forcer.
        if (Math.abs(el.scrollTop - saved.top) < 2) return stop();
      }
      if (Date.now() - start < RESTORE_TIMEOUT_MS) raf = requestAnimationFrame(tick);
      else stop();
    };
    // Si l'utilisateur scrolle lui-même pendant le chargement, on le laisse faire.
    window.addEventListener("wheel", stop, { passive: true });
    window.addEventListener("touchstart", stop, { passive: true });
    window.addEventListener("keydown", stop);
    tick();
    return stop;
  }, [key, navigationType, location.state]);

  return null;
}
